import React, { useState, useEffect } from 'react';
import { ticketAPI } from '../../services/api';
import TicketItem from './TicketItem';
import LoadingSpinner from '../common/LoadingSpinner';
import { showToast } from '../common/Toast';
import './TicketList.css';

/**
 * Ticket List Component - Fetches and displays tickets
 * Shows different views based on user role
 */
const TicketList = ({ isAdmin, refreshTrigger }) => {
  // State for tickets and UI
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  /**
   * Fetch tickets from API
   */
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await ticketAPI.getAll();
      setTickets(response.data.data);
      
    } catch (error) {
      setError('Failed to load tickets');
      showToast.error('Failed to load tickets');
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update ticket status (Admin only)
   * @param {string} ticketId - ID of ticket to update
   * @param {string} newStatus - New status value
   */
  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      setUpdating(true);
      
      // Update status via API
      await ticketAPI.updateStatus(ticketId, newStatus);
      
      // Show success message
      showToast.success(`Ticket status updated to ${newStatus}`);
      
      // Refresh tickets to get updated data
      await fetchTickets();
      
    } catch (error) {
      showToast.error('Failed to update ticket status');
      console.error('Error updating ticket:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Load tickets on component mount and when refreshTrigger changes
  useEffect(() => {
    fetchTickets();
  }, [refreshTrigger]);

  // Show loading state
  if (loading) {
    return (
      <div className="ticket-list">
        <LoadingSpinner message="Loading tickets..." />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="ticket-list">
        <div className="error-message">{error}</div>
        <button 
          className="retry-btn"
          onClick={fetchTickets}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show empty state
  if (tickets.length === 0) {
    return (
      <div className="ticket-list">
        <div className="empty-state">
          <p>No tickets found</p>
          {!isAdmin && (
            <p className="empty-hint">
              Create your first ticket using the form above!
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-list">
      <div className="ticket-list-header">
        <h3>
          {isAdmin ? 'All Tickets' : 'My Tickets'}
          <span className="ticket-count">({tickets.length})</span>
        </h3>
        {isAdmin && (
          <span className="admin-badge">Admin View</span>
        )}
      </div>

      {/* Render each ticket */}
      {tickets.map((ticket) => (
        <TicketItem
          key={ticket._id}
          ticket={ticket}
          isAdmin={isAdmin}
          onStatusUpdate={handleStatusUpdate}
        />
      ))}

      {updating && (
        <div className="updating-overlay">
          Updating ticket status...
        </div>
      )}
    </div>
  );
};

export default TicketList;